import { css } from '@emotion/react';
import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import React, { Fragment, Children, useState } from 'react';
import { Animation } from '../../../theme/Animation';
import { Colors } from '../../../theme/Colors';
import { BaseButton } from '../../navigation/Button/BaseButton';
import { Icon } from '../../display/Icon';
import { Indicator } from '../../navigation/Indicator';
import { Paginator } from '../../navigation/Paginator';
import { ScrollSnap } from './ScrollSnap';
import { uniqueId } from '../../../utils/uniqueId';
const Styles = labelStyles('Carousel', {
    carousel: (props, { isFirstPage, isLastPage }) => [
        css({
            position: 'relative',
        }),
        (props.edgeColor || props.showArrows) &&
            withModifier('fadeEdges', {
                '&::before, &::after': {
                    content: '""',
                    position: 'absolute',
                    zIndex: 1,
                    top: 0,
                    bottom: 0,
                    width: 100,
                    pointerEvents: 'none',
                    transition: Animation.transitionValue,
                },
                '&::before': {
                    opacity: isFirstPage ? 0 : 1,
                    left: 0,
                    right: 'auto',
                    background: `linear-gradient(to right, ${props.edgeColor || Colors.white}, #fff0)`,
                },
                '&::after': {
                    opacity: isLastPage ? 0 : 1,
                    left: 'auto',
                    right: 0,
                    background: `linear-gradient(to left, ${props.edgeColor || Colors.white}, #fff0)`,
                },
            }),
    ],
    paginator: () => [
        css({
            marginTop: 30,
            gridTemplateRows: 'auto auto',
            gridTemplateColumns: '100%',
            justifyItems: 'center',
            display: 'grid',
        }),
    ],
    arrowButton: (props) => [
        css({
            transition: Animation.transitionValue,
            position: 'absolute',
            zIndex: 3,
            top: props.yOffset,
            transform: 'translateY(-50%)',
            display: 'none',
            '&:hover > button': {
                boxShadow: 'none !important',
            },
            '@media (min-width: 1230px)': {
                display: 'block',
            },
        }),
        withModifier(props.direction, {
            prev: {
                left: '20px',
            },
            next: {
                right: '20px',
            },
        }[props.direction]),
        props.isDisabled &&
            withModifier('disabled', {
                pointerEvents: 'none',
                '& > button': {
                    color: `${Colors.neutral100} !important`,
                    border: 0,
                    backgroundColor: `${Colors.neutral500} !important`,
                },
            }),
    ],
    scrollSnapChild: (fadeInactive, isActiveSlide) => [
        css({
            transition: 'opacity 0.5s',
            height: '100%',
        }),
        fadeInactive &&
            !isActiveSlide &&
            withModifier('faded', {
                opacity: 0.3,
            }),
    ],
});
const ArrowButton = (props) => {
    const icon = {
        prev: 'arrowSmallLeft',
        next: 'arrowSmallRight',
    }[props.direction];
    return (isolateComponent("span", { css: Styles.arrowButton(props) },
        isolateComponent(BaseButton, { ariaLabel: `${props.direction} image`, color: "primary-alt", disabled: props.isDisabled, isPill: true, isSquare: true, onClick: props.onClick, size: "large" },
            isolateComponent(Icon, { isBlock: true, name: icon, size: 30 }))));
};
export const Carousel = ({ children, slideWidth, arrowOffset = '50%', edgeColor = undefined, fadeInactive = undefined, gap: _deprecatedGapProp = 20, id = undefined, onActiveSlideChange = undefined, paginatorStyle = undefined, showArrows = undefined, showPaginator = false, }) => {
    const gap = 20;
    const [pageIndex, setPageIndex] = useState(0);
    const isFirstPage = pageIndex === 0;
    const isLastPage = pageIndex === Children.count(children) - 1;
    const changePageIndex = (nextIndex, source) => {
        setPageIndex(nextIndex);
        if (onActiveSlideChange && pageIndex !== nextIndex) {
            onActiveSlideChange(nextIndex, pageIndex, { source });
        }
    };
    const paginatorAriaLabel = id
        ? `${id} carousel paginator`
        : uniqueId('carousel paginator ');
    let paginator = paginatorStyle &&
        {
            paginator: (isolateComponent(Paginator, { ariaLabel: paginatorAriaLabel, currentPageIndex: pageIndex, onPaginate: ({ pageIndex: index }) => changePageIndex(index, 'paginator'), pageUrls: Array.from({ length: Children.count(children) }) })),
            indicator: (isolateComponent(Indicator, { ariaLabel: "List of images", color: "gray", count: Children.count(children), noun: "image", onChangeIndex: (index) => changePageIndex(index, 'indicator'), selectedIndex: pageIndex })),
        }[paginatorStyle];
    if (showPaginator || paginatorStyle) {
        paginator = (isolateComponent(Paginator, { ariaLabel: paginatorAriaLabel, currentPageIndex: pageIndex, onPaginate: ({ pageIndex: index }) => changePageIndex(index, 'paginator'), pageUrls: Array.from({ length: Children.count(children) }) }));
    }
    return (isolateComponent("div", { id: id },
        isolateComponent("div", { css: Styles.carousel({ edgeColor, showArrows }, { isFirstPage, isLastPage }) },
            showArrows && (isolateComponent(Fragment, null,
                isolateComponent(ArrowButton, { direction: "prev", isDisabled: isFirstPage, yOffset: arrowOffset, onClick: () => {
                        changePageIndex(pageIndex - 1, 'previous');
                    } }),
                isolateComponent(ArrowButton, { direction: "next", isDisabled: isLastPage, yOffset: arrowOffset, onClick: () => {
                        changePageIndex(pageIndex + 1, 'next');
                    } }))),
            isolateComponent(ScrollSnap, { gap: gap, itemWidth: slideWidth, onScroll: (index) => {
                    changePageIndex(index, 'scroll');
                }, pageIndex: pageIndex, scrollSnapAlign: "center" }, React.Children.map(children, (child, i) => (isolateComponent("div", { css: Styles.scrollSnapChild(fadeInactive, pageIndex === i), key: i }, child))))),
        paginator && isolateComponent("div", { css: Styles.paginator() }, paginator)));
};
//# sourceMappingURL=Carousel.js.map
// @zapier/design-system@6.27.0 (617a97f69)
